import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  ChevronDown,
  Ticket,
  Mail,
  Sparkles,
  HelpCircle,
} from 'lucide-react';
import SEO, { buildFaqSchema } from '../../components/SEO';
import PartnerAccountLayout from '../../components/PartnerAccountLayout';

// ──────────────────────────────────────────────────────────────────────
// FAQ source of truth — 20 questions covering the Partner Program, Stripe
// Connect, dashboard mechanics, marketing and support paths. Phrased
// exactly as the product owner approved; categorized so we can group them
// in the UI without re-numbering manually.
// ──────────────────────────────────────────────────────────────────────
const FAQS = [
  { n: 1,  cat: 'About NoFee',        q: 'What is NoFee?',
    a: 'NoFee is a restaurant ordering platform that allows restaurants to receive orders without paying delivery marketplace fees on each order.' },
  { n: 2,  cat: 'Partner Program',    q: 'How does the Partner Program work?',
    a: 'Partner Organizations receive a unique referral code and referral link. Restaurants joining through the code are automatically linked to the organization.' },
  { n: 3,  cat: 'Partner Program',    q: 'How much does a Partner Organization earn?',
    a: 'Partner Organizations earn 10% recurring revenue from active referred restaurants according to the current Partner Program terms.' },
  { n: 4,  cat: 'Payments',           q: 'How do I receive my commissions?',
    a: 'Commissions are paid automatically through Stripe Connect.' },
  { n: 5,  cat: 'Payments',           q: 'Why do I need to connect Stripe?',
    a: 'Stripe allows NoFee to securely transfer recurring commissions directly to your bank account.' },
  { n: 6,  cat: 'Security',           q: 'Does NoFee store my banking information?',
    a: 'No. All banking information is securely managed by Stripe Connect.' },
  { n: 7,  cat: 'Referrals',          q: 'How do I get my Partner Code?',
    a: 'Your Partner Code is automatically generated after creating your Partner account.' },
  { n: 8,  cat: 'Referrals',          q: 'How do I share my Partner Code?',
    a: 'You can share your Partner Code or Referral Link with your members.' },
  { n: 9,  cat: 'Referrals',          q: 'How do restaurants join through my organization?',
    a: 'Restaurants enter your Partner Code during registration or use your Referral Link.' },
  { n: 10, cat: 'Dashboard',          q: 'How can I track my referrals?',
    a: 'All referrals are tracked automatically and displayed in your Partner Dashboard.' },
  { n: 11, cat: 'Dashboard',          q: 'What information can I see in the Partner Dashboard?',
    a: '• Restaurants Joined\n• Active Restaurants\n• Restaurants In Trial\n• Monthly Commissions\n• Total Commissions Received\n• Payment History\n• Referral Activity' },
  { n: 12, cat: 'Payments',           q: 'When do commissions get paid?',
    a: 'Commissions are automatically distributed according to the Stripe payout schedule.' },
  { n: 13, cat: 'Marketing',          q: 'Can I download marketing materials?',
    a: 'Yes. Partners have access to flyers, PDFs, email templates, banners and social media graphics.' },
  { n: 14, cat: 'Referrals',          q: 'What happens if a restaurant cancels?',
    a: 'The restaurant remains visible in your referral history. Recurring commissions stop according to the Partner Program terms.' },
  { n: 15, cat: 'Support',            q: 'How do I contact support?',
    a: 'You can submit a support ticket directly through the Partner Dashboard.' },
  { n: 16, cat: 'Account',            q: 'Is there a minimum number of restaurants required?',
    a: 'No. Organizations can join regardless of size.' },
  { n: 17, cat: 'Account',            q: 'How do I update my organization information?',
    a: 'Organization details can be updated from your Partner Dashboard.' },
  { n: 18, cat: 'Account',            q: 'Can multiple people from my organization access the account?',
    a: 'Access permissions depend on the Partner account configuration.' },
  { n: 19, cat: 'Payments',           q: 'How do I know if Stripe is connected?',
    a: 'Your Stripe connection status is displayed inside the Partner Dashboard.' },
  { n: 20, cat: 'Support',            q: 'How do I request a meeting with NoFee?',
    a: 'Most questions can be resolved through the Help Center or Support Ticket system. If additional assistance is required, you may request a meeting with the NoFee team.' },
];

const CATEGORIES = ['About NoFee', 'Partner Program', 'Referrals', 'Dashboard', 'Payments', 'Security', 'Marketing', 'Account', 'Support'];

function FaqRow({ item, open, onToggle }) {
  return (
    <div
      className={`group rounded-2xl border transition-all overflow-hidden ${
        open
          ? 'border-gold-500/40 bg-gradient-to-br from-gold-500/[0.06] to-black/40'
          : 'border-white/[0.08] bg-white/[0.02] hover:border-gold-500/25 hover:bg-white/[0.035]'
      }`}
      data-testid={`faq-row-${item.n}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-5 sm:px-7 py-4 sm:py-5 flex items-start gap-4"
        aria-expanded={open}
        data-testid={`faq-question-${item.n}`}
      >
        <span className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-lg bg-gold-500/10 border border-gold-500/25 text-gold-300 text-[11.5px] font-bold flex items-center justify-center">
          {item.n}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[10px] uppercase tracking-[0.22em] text-gold-400 font-bold mb-1">{item.cat}</span>
          <span className="block text-[15.5px] sm:text-[16.5px] font-bold text-white leading-snug">
            {item.q}
          </span>
        </span>
        <ChevronDown className={`mt-1 w-5 h-5 text-white/45 transition-transform flex-shrink-0 ${open ? 'rotate-180 text-gold-300' : ''}`} />
      </button>
      {open && (
        <div className="px-5 sm:px-7 pb-6 -mt-1" data-testid={`faq-answer-${item.n}`}>
          <div className="pl-11 sm:pl-11 text-[14.5px] text-white/75 leading-relaxed whitespace-pre-line">
            {item.a}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PartnersHelpCenter() {
  return <Inner />;
}

function Inner() {
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState('All');
  const [openId, setOpenId] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) => {
      if (activeCat !== 'All' && f.cat !== activeCat) return false;
      if (!q) return true;
      return (
        f.q.toLowerCase().includes(q) ||
        f.a.toLowerCase().includes(q) ||
        f.cat.toLowerCase().includes(q)
      );
    });
  }, [query, activeCat]);

  return (
    <PartnerAccountLayout
      currentKey="help"
      title="Help Center"
      subtitle="Answers to the most common questions about the Partner Program, Stripe Connect, commissions and your dashboard."
    >
      <SEO
        title="Help Center — NoFee Partners"
        description="Answers to the most common questions about the NoFee Partner Program, Stripe Connect commissions, referrals, dashboard metrics and support."
        path="/partners/help"
        jsonLd={buildFaqSchema(FAQS.map((f) => ({ question: f.q, answer: f.a })))}
      />

      {/* Search bar */}
      <div className="relative mb-6">
        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-white/45" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpenId(null); }}
          placeholder="Search Help Center..."
          className="w-full h-13 pl-14 pr-6 py-3 rounded-2xl bg-white/[0.04] border border-white/[0.10] text-white placeholder-white/45 text-[15px] focus:outline-none focus:border-gold-500/50 transition-colors"
          data-testid="help-center-search"
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(''); setOpenId(null); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-white/55 hover:text-white"
            data-testid="help-center-clear-search"
          >
            CLEAR
          </button>
        )}
      </div>

      {/* Category pills */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="help-center-categories">
        {['All', ...CATEGORIES].map((c) => {
          const isActive = activeCat === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => { setActiveCat(c); setOpenId(null); }}
              data-testid={`help-cat-${c.toLowerCase().replace(/\s+/g, '-')}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-all whitespace-nowrap ${
                isActive
                  ? 'bg-gold-500 text-black border-gold-500'
                  : 'bg-white/[0.03] text-white/65 border-white/[0.08] hover:bg-white/[0.06] hover:text-white hover:border-gold-500/30'
              }`}
            >
              {c}
            </button>
          );
        })}
      </div>

      {/* FAQ list */}
      {filtered.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-white/[0.10] bg-white/[0.015] p-10 text-center"
          data-testid="help-center-empty"
        >
          <HelpCircle className="w-7 h-7 text-white/40 mx-auto mb-3" />
          <p className="text-base font-bold text-white mb-1">No results for &ldquo;{query}&rdquo;</p>
          <p className="text-sm text-white/55 max-w-[420px] mx-auto">
            Try different keywords, browse a category above, or submit a support ticket and we&rsquo;ll get back to you.
          </p>
          <div className="mt-5 flex flex-wrap gap-3 justify-center">
            <Link
              to="/partners/support/ticket"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 text-black font-extrabold text-[13.5px]"
              data-testid="help-center-empty-ticket"
            >
              <Ticket className="w-4 h-4" /> Submit a ticket
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="help-center-faq-list">
          {filtered.map((f) => (
            <FaqRow
              key={f.n}
              item={f}
              open={openId === f.n}
              onToggle={() => setOpenId((p) => (p === f.n ? null : f.n))}
            />
          ))}
        </div>
      )}

      {/* Footer CTA */}
      <div
        className="mt-10 rounded-3xl border border-gold-500/25 bg-gradient-to-br from-zinc-900 to-black p-7 lg:p-10"
        data-testid="help-center-footer-cta"
      >
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-7 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-bold mb-3 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Still need help?
            </p>
            <h2 className="text-2xl lg:text-3xl font-black tracking-tight text-white leading-tight">
              Open a ticket or talk to our Partnership Team.
            </h2>
            <p className="mt-3 text-white/60 max-w-[540px] leading-relaxed">
              We typically respond within one business day. For account-specific issues, please submit a ticket so we can look at your data directly.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/partners/support/ticket"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold text-[13.5px] transition-all"
              data-testid="help-center-cta-ticket"
            >
              <Ticket className="w-4 h-4" /> Submit a ticket
            </Link>
            <Link
              to="/partners/support/contact"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 hover:border-gold-500/40 text-white font-bold text-[13.5px] transition-all"
              data-testid="help-center-cta-contact"
            >
              <Mail className="w-4 h-4" /> Contact Support
            </Link>
          </div>
        </div>
      </div>
    </PartnerAccountLayout>
  );
}
